/* eslint-disable no-empty-function */

'use client';

import { createContext, useMemo, useContext } from 'react';
import useLoginModal from '@fe-news/hooks/use-login-modal';
import useMyCollection from '@fe-news/hooks/use-my-collection';
import { useQueries } from '@tanstack/react-query';
import { fetchPopTopic } from '@fe-news/actions/pop-topic';

// type
import { MyCollectionProductType, ReturnMyCollectionType } from '@fe-news/constants/my-track/type';
import type { PopTopicDataWithPage } from '@fe-news/constants/pop-topic/type';
import type { Response } from '@fe-news/constants/api';

type MyPopTopicContextValue = {
  isLoggedIn: boolean;
  isCloseLoginModal: boolean;
  onClickLoginModalOpen: () => void;
  onClickLoginModalClose: () => void;
  list?: ReturnMyCollectionType;
  isLoading: boolean;
  myCollectionDataItemsIdArray: string[];
  refetchMyCollection: () => Promise<void>;
  myPopTopicData: Map<string, Response<PopTopicDataWithPage> | undefined>;
};

const MyPopTopicContext = createContext<MyPopTopicContextValue | undefined>(undefined);

export default function MyPopTopicProvider({ children }: { children: React.ReactNode }) {
  const myPopTopicData = new Map<string, Response<PopTopicDataWithPage> | undefined>();

  const { isLoggedIn, isCloseLoginModal, onClickLoginModalOpen, onClickLoginModalClose } = useLoginModal({
    defaultCloseLoginModal: false
  });

  const { isLoading, refetch, myCollectionDataItemsIdArray } = useMyCollection({
    productType: MyCollectionProductType.POPTOPIC,
    isLogin: isLoggedIn
  });

  const refetchMyCollection = async () => {
    await refetch();
  };

  const queries = useQueries({
    queries: (myCollectionDataItemsIdArray || []).map(id => ({
      queryKey: ['my-pop-topic', `id:${id}`],
      queryFn: () => fetchPopTopic({ id: Number(id) })
    }))
  });

  queries.forEach(query => {
    myPopTopicData.set(query.data?.data?.popularTopic.id?.toString() ?? '0', query.data);
  });

  const value = useMemo(
    () => ({
      isLoggedIn,
      isCloseLoginModal,
      onClickLoginModalOpen,
      onClickLoginModalClose,
      isLoading,
      myCollectionDataItemsIdArray,
      refetchMyCollection,
      myPopTopicData
    }),
    [
      isLoggedIn,
      isCloseLoginModal,
      onClickLoginModalOpen,
      onClickLoginModalClose,
      isLoading,
      myCollectionDataItemsIdArray,
      refetchMyCollection,
      queries
    ]
  );

  return <MyPopTopicContext.Provider value={value}>{children}</MyPopTopicContext.Provider>;
}

export const useMyPopTopic = () => {
  const context = useContext(MyPopTopicContext);
  if (!context) {
    throw new Error('useMyPopTopic must be used within a MyPopTopicProvider');
  }
  return context;
};
